// Pure aggregation for the Timesheet grid — kept component-free (no React/@pulse/ui imports)
// so it stays trivially unit-testable, matching projectCompletion.ts/resourceLevelling.ts.
// The backend deliberately returns a flat `workLogs` list rather than pre-aggregating; every
// day/task/project total shown in the grid is derived here instead.

import type { IIssueWorkLog, ITimesheetTaskRow } from '@pulse/shared';

// Appending a time component (no timezone offset) makes the Date constructor parse this as
// local midnight, not UTC — the date-only ISO form ("YYYY-MM-DD" alone) parses as UTC per spec,
// which is the footgun this sidesteps. Same convention as the backend's toLocalIsoDate helpers.
export function parseLocalDate(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** The 7 ISO dates (Mon→Sun) of the week starting on `weekStartIso`. */
export function buildDayColumns(weekStartIso: string): string[] {
  const start = parseLocalDate(weekStartIso);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return toIsoDate(day);
  });
}

export interface ProjectTaskGroup {
  projectId: string;
  projectName: string;
  tasks: ITimesheetTaskRow[];
}

/** Groups task rows by project (preserving first-seen order), for the grid's project header
 *  rows and the mobile card list's project sections. */
export function groupTaskRowsByProject(taskRows: readonly ITimesheetTaskRow[]): ProjectTaskGroup[] {
  const groups = new Map<string, ProjectTaskGroup>();
  for (const row of taskRows) {
    const group = groups.get(row.projectId);
    if (group) group.tasks.push(row);
    else groups.set(row.projectId, { projectId: row.projectId, projectName: `${row.projectKey} — ${row.projectName}`, tasks: [row] });
  }
  return [...groups.values()];
}

/** All entries logged against `issueId` on `dateIso`. */
export function entriesForCell(workLogs: readonly IIssueWorkLog[], issueId: string, dateIso: string): IIssueWorkLog[] {
  return workLogs.filter((w) => w.issueId === issueId && w.date === dateIso);
}

/** Total hours logged against `issueId` on `dateIso`. */
export function cellTotal(workLogs: readonly IIssueWorkLog[], issueId: string, dateIso: string): number {
  return entriesForCell(workLogs, issueId, dateIso).reduce((sum, w) => sum + w.hours, 0);
}

/** Total hours logged against `issueId` across every entry (not just the displayed week). */
export function taskRowTotal(workLogs: readonly IIssueWorkLog[], issueId: string): number {
  return workLogs.filter((w) => w.issueId === issueId).reduce((sum, w) => sum + w.hours, 0);
}

/** Total hours logged on `dateIso` across every task. */
export function dayColumnTotal(workLogs: readonly IIssueWorkLog[], dateIso: string): number {
  return workLogs.filter((w) => w.date === dateIso).reduce((sum, w) => sum + w.hours, 0);
}

/** Total hours logged across the whole (already week-scoped) `workLogs` list. */
export function weekTotal(workLogs: readonly IIssueWorkLog[]): number {
  return workLogs.reduce((sum, w) => sum + w.hours, 0);
}

/** Total hours logged on `dateIso`, restricted to `issueIds` (e.g. one project's tasks) — the
 *  per-project day subtotal row in TimesheetGrid. */
export function projectDayTotal(workLogs: readonly IIssueWorkLog[], issueIds: readonly string[], dateIso: string): number {
  const idSet = new Set(issueIds);
  return workLogs.filter((w) => w.date === dateIso && idSet.has(w.issueId)).reduce((sum, w) => sum + w.hours, 0);
}

/** Total hours logged across the whole week, restricted to `issueIds` (e.g. one project's
 *  tasks) — the per-project total in TimesheetGrid's subtotal row. */
export function projectTotal(workLogs: readonly IIssueWorkLog[], issueIds: readonly string[]): number {
  const idSet = new Set(issueIds);
  return workLogs.filter((w) => idSet.has(w.issueId)).reduce((sum, w) => sum + w.hours, 0);
}

/** Free-text block (work log description, rejection reason): shows up to 4 lines, then scrolls
 *  inside its box instead of growing the row/panel. */
export const TEXT_SCROLL_4_LINES_SX = {
  lineHeight: 1.4,
  maxHeight: '5.6em',
  overflowY: 'auto',
  wordBreak: 'break-word',
} as const;

/** Hours total for display: a whole number shows with no decimals ("8"), anything fractional
 *  shows to two places ("7.50"). Rounds to 2dp first so float noise (0.1 + 0.2) can't leak. */
export function formatTotalHours(hours: number): string {
  const rounded = Math.round(hours * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}
